import { useEffect, useState } from 'react';
import api from '../lib/api';
import { useToast } from '../lib/toast';

const C = {
  navy: '#0D1B3E',
  red: '#E63946',
  green: '#10B981',
  amber: '#F59E0B',
  blue: '#3B82F6',
  ink: '#0F172A',
  muted: '#64748B',
  faint: '#94A3B8',
  border: '#E2E8F0',
  bgSoft: '#F8FAFC',
  bg: '#F1F5F9',
};

const PRIORITY_COL = { Critical: '#EF4444', High: '#F59E0B', Medium: '#3B82F6', Low: '#10B981' };
const STATUS_COL = { Pending: '#F59E0B', Confirmed: '#3B82F6', Completed: '#10B981', Cancelled: '#94A3B8' };

const money = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ReportsView() {
  const { show } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/reports/summary');
      setData(data);
    } catch (e) {
      show('Failed to load reports', 'error');
    } finally {
      setLoading(false);
    }
  }

  async function exportCsv() {
    try {
      const res = await api.get('/reports/export/bookings.csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `rentcarsus-bookings-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      show('CSV downloaded', 'success');
    } catch (e) {
      show('Export failed', 'error');
    }
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 80, color: C.faint }}>Loading reports…</div>;
  if (!data) return <div style={{ textAlign: 'center', padding: 80, color: C.faint }}>No data.</div>;

  const maxTrend = Math.max(1, ...data.revenue.trend_14_days.map((d) => d.revenue));

  return (
    <div data-testid="reports-view">
      {/* Top action bar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12, gap: 10 }}>
        <button
          data-testid="export-csv-btn"
          onClick={exportCsv}
          style={{ padding: '8px 14px', borderRadius: 8, border: `1.5px solid ${C.border}`, background: '#fff', fontWeight: 700, fontSize: 12, color: C.navy, cursor: 'pointer' }}
        >
          ⬇ Export bookings (CSV)
        </button>
      </div>
      {/* Top revenue row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 22 }}>
        <Stat testId="rep-total-rev" label="Total Revenue" value={money(data.revenue.total)} icon="💰" color={C.navy} sub={`${data.bookings.paid_count} paid bookings`} />
        <Stat testId="rep-rev-7" label="Last 7 days" value={money(data.revenue.last_7_days)} icon="📈" color={C.green} />
        <Stat testId="rep-rev-30" label="Last 30 days" value={money(data.revenue.last_30_days)} icon="📊" color={C.blue} />
        <Stat testId="rep-utilisation" label="Fleet Utilisation" value={`${data.fleet.utilisation_pct}%`} icon="🚗" color={C.red} sub={`${data.fleet.available} of ${data.fleet.total} available`} />
      </div>

      {/* Revenue trend */}
      <Section title="Revenue trend — last 14 days" right={maxTrend > 0 ? `Peak ${money(maxTrend)}` : 'No paid bookings yet'}>
        <div data-testid="rev-trend" style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 160, padding: '12px 4px 0' }}>
          {data.revenue.trend_14_days.map((d) => {
            const pct = maxTrend > 0 ? (d.revenue / maxTrend) * 100 : 0;
            const day = d.date.slice(8, 10);
            return (
              <div key={d.date} title={`${d.date}: ${money(d.revenue)}`} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div
                  style={{
                    width: '100%',
                    height: `${Math.max(2, pct)}%`,
                    background: d.revenue > 0 ? `linear-gradient(180deg, ${C.red} 0%, ${C.navy} 100%)` : C.border,
                    borderRadius: '6px 6px 2px 2px',
                    transition: 'all .3s ease',
                  }}
                />
                <div style={{ fontSize: 10, color: C.faint, fontWeight: 700 }}>{day}</div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Two-column row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16, marginTop: 22 }}>
        {/* Top vehicles */}
        <Section title="Top revenue vehicles" right={`${data.top_vehicles.length} of paid fleet`}>
          {data.top_vehicles.length === 0 ? (
            <Empty msg="No paid bookings yet. Top-grossing vehicles will show here." />
          ) : (
            <div data-testid="top-vehicles" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {data.top_vehicles.map((v, i) => {
                const max = data.top_vehicles[0].revenue || 1;
                const pct = (v.revenue / max) * 100;
                return (
                  <div key={v.vid} style={{ display: 'grid', gridTemplateColumns: '28px 1fr auto', gap: 12, alignItems: 'center' }}>
                    <div style={{ fontSize: 20, textAlign: 'center' }}>{v.emoji}</div>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <span style={{ fontWeight: 800, color: C.ink, fontSize: 13 }}>{i + 1}. {v.label}</span>
                        <span style={{ fontSize: 11, color: C.faint }}>{v.bookings} booking{v.bookings === 1 ? '' : 's'}</span>
                      </div>
                      <div style={{ marginTop: 4, height: 8, background: C.bg, borderRadius: 999, overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: v.color, transition: 'width .4s ease' }} />
                      </div>
                    </div>
                    <div style={{ fontWeight: 900, color: C.navy, fontSize: 14, minWidth: 80, textAlign: 'right' }}>{money(v.revenue)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        {/* Booking status breakdown */}
        <Section title="Bookings by status">
          <DonutBreakdown data={data.bookings.by_status} colorMap={STATUS_COL} testId="bookings-breakdown" />
        </Section>
      </div>

      {/* Maintenance row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginTop: 22 }}>
        <Stat testId="maint-spend-total" label="Maintenance spend (all-time)" value={money(data.maintenance.spend_total)} icon="🔧" color={C.amber} />
        <Stat testId="maint-spend-30" label="Maintenance spend (30 days)" value={money(data.maintenance.spend_last_30_days)} icon="🛠️" color={C.red} />
        <Stat testId="maint-open" label="Open work orders" value={data.maintenance.open_total} icon="📋" color={C.navy} sub="Across all priorities" />
      </div>

      <div style={{ marginTop: 22 }}>
        <Section title="Open maintenance by priority">
          {data.maintenance.open_total === 0 ? (
            <Empty msg="🎉 No open maintenance work orders." />
          ) : (
            <DonutBreakdown data={data.maintenance.open_by_priority} colorMap={PRIORITY_COL} testId="priority-breakdown" />
          )}
        </Section>
      </div>

      {data.reviews && (
        <div style={{ marginTop: 22 }}>
          <Section title="Top-rated vehicles" right={`${data.reviews.total} review${data.reviews.total === 1 ? '' : 's'} total`}>
            {data.reviews.top_rated.length === 0 ? (
              <Empty msg="No reviews yet. After a completed trip, customers can leave a 1-5 ⭐ rating." />
            ) : (
              <div data-testid="top-rated-vehicles" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {data.reviews.top_rated.map((v, i) => (
                  <div key={v.vid} style={{ display: 'grid', gridTemplateColumns: '28px 1fr auto', gap: 12, alignItems: 'center' }}>
                    <div style={{ fontSize: 20, textAlign: 'center' }}>{v.emoji}</div>
                    <div>
                      <div style={{ fontWeight: 800, color: C.ink, fontSize: 13 }}>{i + 1}. {v.label}</div>
                      <div style={{ fontSize: 11, color: C.faint, fontWeight: 600 }}>{v.review_count} review{v.review_count === 1 ? '' : 's'}</div>
                    </div>
                    <div style={{ fontWeight: 900, color: '#F59E0B', fontSize: 14, minWidth: 80, textAlign: 'right' }}>
                      {'★'.repeat(Math.round(v.avg_rating))}
                      <span style={{ color: C.muted, fontSize: 12, marginLeft: 4 }}>{v.avg_rating.toFixed(1)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      )}

      <p style={{ marginTop: 18, fontSize: 11, color: C.faint, textAlign: 'right' }}>
        Last refreshed {new Date(data.generated_at).toLocaleTimeString()} ·{' '}
        <button onClick={load} data-testid="reports-refresh" style={{ background: 'none', border: 'none', color: C.navy, fontWeight: 700, cursor: 'pointer', fontSize: 11, textDecoration: 'underline' }}>
          refresh
        </button>
      </p>
    </div>
  );
}

function Stat({ label, value, icon, color, sub, testId }) {
  return (
    <div data-testid={testId} style={{ background: '#fff', borderRadius: 14, border: `1px solid ${C.border}`, padding: '18px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase' }}>{label}</div>
        <span style={{ fontSize: 18 }}>{icon}</span>
      </div>
      <div style={{ fontSize: 26, fontWeight: 900, color, lineHeight: 1.1, marginTop: 8 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.faint, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Section({ title, right, children }) {
  return (
    <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${C.border}`, padding: '18px 22px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
        <h3 style={{ fontSize: 13, fontWeight: 800, color: C.ink, margin: 0, letterSpacing: '-.01em' }}>{title}</h3>
        {right && <span style={{ fontSize: 11, color: C.faint, fontWeight: 600 }}>{right}</span>}
      </div>
      {children}
    </div>
  );
}

function Empty({ msg }) {
  return <div style={{ padding: '36px 12px', textAlign: 'center', color: C.faint, fontSize: 13 }}>{msg}</div>;
}

function DonutBreakdown({ data, colorMap, testId }) {
  const entries = Object.entries(data);
  const total = entries.reduce((s, [, v]) => s + v, 0);
  if (total === 0) return <Empty msg="No data yet." />;
  // Conic gradient
  let acc = 0;
  const stops = entries
    .map(([k, v]) => {
      const start = (acc / total) * 360;
      acc += v;
      const end = (acc / total) * 360;
      return `${colorMap[k] || C.muted} ${start}deg ${end}deg`;
    })
    .join(', ');

  return (
    <div data-testid={testId} style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
      <div
        style={{
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: `conic-gradient(${stops})`,
          position: 'relative',
          flexShrink: 0,
        }}
      >
        <div style={{ position: 'absolute', inset: 18, background: '#fff', borderRadius: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: 22, fontWeight: 900, color: C.navy, lineHeight: 1 }}>{total}</span>
          <span style={{ fontSize: 10, color: C.faint, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase' }}>Total</span>
        </div>
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {entries.map(([k, v]) => (
          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: colorMap[k] || C.muted, flexShrink: 0 }} />
            <span style={{ flex: 1, color: C.ink, fontWeight: 600 }}>{k}</span>
            <span style={{ color: C.muted, fontWeight: 700, fontSize: 12 }}>{v}</span>
            <span style={{ color: C.faint, fontSize: 11, minWidth: 36, textAlign: 'right' }}>{total ? Math.round((v / total) * 100) : 0}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}
